import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Sparkles } from "lucide-react";
import { HeroScene } from "./HeroScene";
import { EASE } from "./Reveal";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1.1, ease: EASE, delay: 0.35 + i * 0.16 },
  }),
};

export const Hero = ({ onEnquire }) => (
  <section
    id="home"
    data-testid="hero-section"
    className="relative min-h-screen flex items-center overflow-hidden"
  >
    <div className="absolute -top-32 -left-32 w-[34rem] h-[34rem] rounded-full bg-apricot/60 blur-3xl" />
    <div className="absolute top-1/3 -right-40 w-[38rem] h-[38rem] rounded-full bg-mint/80 blur-3xl" />
    <div className="absolute bottom-0 left-1/3 w-[26rem] h-[26rem] rounded-full bg-sun/20 blur-3xl" />
    <div className="absolute inset-0">
      <HeroScene />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-32 pb-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
        className="inline-flex items-center gap-2 glass rounded-full pl-2 pr-4 py-1.5 mb-8"
      >
        <span className="flex items-center gap-1.5 bg-sun text-ink text-[11px] font-play font-semibold px-3 py-1 rounded-full">
          <Sparkles className="w-3 h-3" />
          Admissions Open
        </span>
        <span className="text-xs sm:text-sm text-ink/70 font-medium">
          Kiwi Kids Pre-School · Ages 1.5 – 6
        </span>
      </motion.div>

      <h1 className="font-display font-extrabold tracking-tight text-ink leading-[0.98] text-[clamp(2.9rem,8.5vw,7rem)] max-w-4xl">
        <span className="mask-line">
          <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
            Little steps.
          </motion.span>
        </span>
        <span className="mask-line">
          <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block">
            Big{" "}
            <em className="font-quote italic font-medium text-sage">beginnings</em>
            <span className="text-peach">.</span>
          </motion.span>
        </span>
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
        className="mt-7 text-base sm:text-lg text-ink/65 max-w-xl leading-relaxed"
      >
        A joyful start to early learning — where every day begins with play,
        stories, creativity and the thrill of discovery.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1, ease: EASE }}
        className="mt-10 flex flex-wrap items-center gap-4"
      >
        <motion.button
          data-testid="hero-cta-enquire-admissions"
          onClick={onEnquire}
          whileHover={{ y: -3 }}
          whileTap={{ scale: 0.97 }}
          className="group inline-flex items-center gap-2.5 bg-peach text-white font-semibold text-base px-8 py-4 rounded-full shadow-[0_16px_36px_rgba(255,122,89,0.4)] hover:bg-ink transition-colors duration-300"
        >
          Enquire about admissions
          <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
        </motion.button>
        <motion.button
          data-testid="hero-cta-explore-programmes"
          onClick={() =>
            window.__lenis
              ? window.__lenis.scrollTo("#programs", { offset: -80, duration: 1.4 })
              : document.querySelector("#programs")?.scrollIntoView({ behavior: "smooth" })
          }
          whileHover={{ y: -3 }}
          whileTap={{ scale: 0.97 }}
          className="inline-flex items-center gap-2.5 glass text-ink font-semibold text-base px-8 py-4 rounded-full hover:bg-white transition-colors"
        >
          Explore our programmes
        </motion.button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="mt-12 flex flex-wrap gap-2.5"
      >
        {["Ages 1.5 – 6 years", "Play-based curriculum", "Safe & nurturing"].map((b) => (
          <span
            key={b}
            className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide uppercase text-sage bg-mint/80 border border-sage/15 rounded-full px-4 py-2"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-sage" />
            {b}
          </span>
        ))}
      </motion.div>
    </div>

    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.6, duration: 0.8 }}
      className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-ink/50"
    >
      <span className="text-[10px] font-semibold tracking-[0.3em] uppercase">Scroll</span>
      <motion.span animate={{ y: [0, 7, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
        <ArrowDown className="w-4 h-4" />
      </motion.span>
    </motion.div>
  </section>
);
